import { Button, Modal } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentSuggestionCard } from 'src/Components/Documents/DocumentSuggestionCard';
import { SuggestionOverrides } from 'src/Components/Documents/DocumentSuggestionCard/DocumentSuggestionCard.types';
import { useAcceptDocumentSuggestion } from 'src/Hooks/Mutations/useAcceptDocumentSuggestion';
import { useRejectDocumentSuggestion } from 'src/Hooks/Mutations/useRejectDocumentSuggestion';
import { DocumentSuggestionDto } from 'src/Services/API/generated';
import {
  trackDocumentSuggestionAccepted,
  trackDocumentSuggestionRejected,
} from 'src/Services/Mixpanel/events';

import styles from './DocumentSuggestionsModal.module.scss';
import { Props } from './DocumentSuggestionsModal.types';

// t('documents.suggestions.acceptAll_one', 'Accepter la proposition')
// t('documents.suggestions.acceptAll_other', 'Tout accepter ({{count}})')

const DocumentSuggestionsModal = ({ open, onClose, suggestions }: Props) => {
  const { t } = useTranslation();
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [acceptingAll, setAcceptingAll] = useState(false);

  const { mutateAsync: accept } = useAcceptDocumentSuggestion();
  const { mutateAsync: reject } = useRejectDocumentSuggestion();

  useEffect(() => {
    if (open && suggestions.length === 0 && !acceptingAll) {
      onClose();
    }
  }, [open, suggestions.length, acceptingAll, onClose]);

  const withBusy = async (id: string, action: () => Promise<unknown>) => {
    setBusyIds(previous => new Set(previous).add(id));
    try {
      await action();
    } finally {
      setBusyIds(previous => {
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
    }
  };

  const showError = () =>
    notifications.show({
      color: 'red',
      message: t(
        'documents.suggestions.error',
        "Ce document n'a pas pu être rangé. Il a peut-être été modifié entre-temps."
      ),
    });

  const acceptOne = async (
    suggestion: DocumentSuggestionDto,
    overrides: SuggestionOverrides,
    bulk: boolean
  ) => {
    await accept({ suggestionId: suggestion.id, ...overrides });
    const finalPath = overrides.path ?? suggestion.proposedPath;
    trackDocumentSuggestionAccepted({
      document_type: suggestion.documentType,
      edited: overrides.name !== undefined || overrides.path !== undefined,
      bulk,
      moved: finalPath !== undefined && finalPath !== suggestion.originalPath,
    });
  };

  const handleAccept = (suggestion: DocumentSuggestionDto, overrides: SuggestionOverrides) =>
    withBusy(suggestion.id, () => acceptOne(suggestion, overrides, false)).catch(showError);

  const handleReject = (suggestion: DocumentSuggestionDto) =>
    withBusy(suggestion.id, async () => {
      await reject(suggestion.id);
      trackDocumentSuggestionRejected({ document_type: suggestion.documentType });
    }).catch(showError);

  const handleAcceptAll = async () => {
    setAcceptingAll(true);
    let failures = 0;
    // Sequential: suggestions may create the same folder (e.g. /Factures/2026/).
    for (const suggestion of suggestions) {
      try {
        await withBusy(suggestion.id, () => acceptOne(suggestion, {}, true));
      } catch {
        failures++;
      }
    }
    setAcceptingAll(false);
    if (failures > 0) {
      showError();
    } else {
      notifications.show({
        color: 'green',
        message: t('documents.suggestions.allDone', 'Vos documents ont été rangés'),
      });
    }
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      size="lg"
      title={t('documents.suggestions.modalTitle', 'Ranger mes documents')}
    >
      <p className={styles.intro}>
        {t(
          'documents.suggestions.modalIntro',
          'Nous avons lu vos documents et vous proposons un nom clair et un dossier. Rien ne change sans votre accord.'
        )}
      </p>
      <div className={styles.list}>
        {suggestions.map(suggestion => (
          <DocumentSuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            loading={busyIds.has(suggestion.id)}
            onAccept={overrides => handleAccept(suggestion, overrides)}
            onReject={() => handleReject(suggestion)}
          />
        ))}
      </div>
      {suggestions.length > 1 && (
        <div className={styles.footer}>
          <Button size="md" variant="outline" onClick={onClose} disabled={acceptingAll}>
            {t('documents.suggestions.later', 'Plus tard')}
          </Button>
          <Button size="md" onClick={handleAcceptAll} loading={acceptingAll}>
            {t('documents.suggestions.acceptAll', { count: suggestions.length })}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default DocumentSuggestionsModal;
